import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import * as stylex from '@stylexjs/stylex';
import { Alert, AuthCard } from '@sentinel/ui';
import { isSafeReturnTo, useUser } from '@sentinel/auth-core';

// One-shot read of the bridge-handoff slot. /login stashes the setup_token
// here when the magic-link flow returned MfaEnrollmentRequired, so we never
// need to put it on the SPA URL.
function consumeMfaSetupTokenFromSessionStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem('mfa_setup_token');
    if (v) window.sessionStorage.removeItem('mfa_setup_token');
    return v;
  } catch {
    return null;
  }
}

function consumeMfaReturnToFromSessionStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const v = window.sessionStorage.getItem('mfa_return_to');
    if (v) window.sessionStorage.removeItem('mfa_return_to');
    return v;
  } catch {
    return null;
  }
}
import { TotpStep } from '../features/mfa/components/totp-step';
import { WebAuthnStep } from '../features/mfa/components/webauthn-step';
import { RecoveryCodesStep } from '../features/mfa/components/recovery-codes-step';
import { useTotpEnroll } from '../features/mfa/use-totp';
import { useWebAuthnMfaRegister } from '../features/mfa/use-webauthn-mfa';
import { sentinel } from '@sentinel/ui/theme/semantic.stylex';

const tabStyles = stylex.create({
  bar: {
    display: 'flex',
    gap: '0.25rem',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: sentinel.borderDefault,
  },
  tab: {
    appearance: 'none',
    backgroundColor: 'transparent',
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderLeftStyle: 'none',
    borderBottomWidth: '2px',
    borderBottomStyle: 'solid',
    borderBottomColor: 'transparent',
    padding: '0.5rem 0.75rem',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 400,
    color: 'inherit',
  },
  tabActive: {
    fontWeight: 600,
    borderBottomColor: sentinel.accentAction,
  },
});

type Tab = 'totp' | 'webauthn';
type Step = 'enroll' | 'recovery_codes' | 'done';

const searchSchema = z.object({
  setup_token: z.string().optional(),
  return_to: z.string().optional(),
});

interface ErrorBody {
  error?: string;
  message?: string;
}
function getStatus(e: unknown): number | undefined {
  return (e as { status?: number } | null)?.status;
}
function getBody(e: unknown): ErrorBody | undefined {
  if (e && typeof e === 'object') {
    const maybe = e as { body?: ErrorBody } & ErrorBody;
    return maybe.body ?? maybe;
  }
  return undefined;
}

function MfaSetupPage() {
  const { t } = useTranslation('mfa');
  const search = Route.useSearch();
  const user = useUser();
  // Prefer URL query param (preserves backward compat), fall back to the
  // sessionStorage handoff filled by /login's bridge consumer.
  const setupToken = useMemo(
    () => search.setup_token ?? consumeMfaSetupTokenFromSessionStorage(),
    [search.setup_token],
  );
  const sessionReturnTo = useMemo(
    () => (search.return_to ? null : consumeMfaReturnToFromSessionStorage()),
    [search.return_to],
  );
  const returnTo = useMemo(() => {
    const candidate = search.return_to ?? sessionReturnTo ?? undefined;
    return isSafeReturnTo(candidate) ? candidate : '/launch';
  }, [search.return_to, sessionReturnTo]);
  const isSetupTokenFlow = Boolean(setupToken);

  const [tab, setTab] = useState<Tab>('totp');
  const [step, setStep] = useState<Step>('enroll');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [topError, setTopError] = useState<string | null>(null);
  const [totpInlineError, setTotpInlineError] = useState<string | null>(null);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);

  const { enroll, confirm } = useTotpEnroll({ setupToken });
  const webauthn = useWebAuthnMfaRegister();
  const [webauthnStatus, setWebauthnStatus] = useState<
    'idle' | 'pending' | 'success' | 'error'
  >('idle');
  const [webauthnError, setWebauthnError] = useState<string | null>(null);

  const supportsWebAuthn =
    typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    typeof navigator.credentials?.create === 'function';

  // Redirect SSO-only users — they have no local factors to set up.
  useEffect(() => {
    if (user?.localAuthManagedByIdp) {
      window.location.assign('/account/security');
    }
  }, [user?.localAuthManagedByIdp]);

  // Kick off enrollment automatically on the TOTP tab.
  useEffect(() => {
    // SSO-only users are being redirected away (see effect above); don't fire
    // a doomed enroll request that the backend rejects with 403.
    if (user?.localAuthManagedByIdp) return;
    if (tab !== 'totp' || step !== 'enroll' || enroll.data || enroll.isPending) return;
    setTopError(null);
    setTotpInlineError(null);
    setAlreadyEnrolled(false);
    enroll.mutate(undefined, {
      onError: (err) => {
        const status = getStatus(err);
        if (status === 409) {
          setAlreadyEnrolled(true);
          return;
        }
        if (status === 401) {
          setTopError(t('setup_session_expired'));
          return;
        }
        setTopError(t('totp_start_failed'));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, step]);

  // Switching tabs resets in-progress state.
  function switchTab(next: Tab) {
    if (next === tab) return;
    setTab(next);
    setStep('enroll');
    setRecoveryCodes([]);
    setTopError(null);
    setTotpInlineError(null);
    setAlreadyEnrolled(false);
    setWebauthnStatus('idle');
    setWebauthnError(null);
    enroll.reset();
    confirm.reset();
    webauthn.reset();
  }

  async function onTotpSubmit(code: string) {
    setTotpInlineError(null);
    setTopError(null);
    try {
      const result = await confirm.mutateAsync({ code });
      setRecoveryCodes(result.recovery_codes ?? []);
      setStep('recovery_codes');
    } catch (e) {
      const status = getStatus(e);
      const body = getBody(e);
      if (status === 401) {
        if (isSetupTokenFlow && body?.message?.toLowerCase().includes('token')) {
          setTopError(t('setup_session_expired'));
          return;
        }
        setTotpInlineError(t('code_invalid'));
        return;
      }
      if (status === 400) {
        setTotpInlineError(t('code_invalid'));
        return;
      }
      setTopError(t('generic_error'));
    }
  }

  async function onWebAuthnRegister() {
    setWebauthnStatus('pending');
    setWebauthnError(null);
    try {
      await webauthn.mutateAsync({ name: 'Security key' });
      setWebauthnStatus('success');
    } catch (e) {
      const body = getBody(e);
      // User-cancelled / aborted ceremony surfaces as DOMException; show generic.
      const message =
        (e instanceof DOMException && e.name === 'NotAllowedError')
          ? t('webauthn_failed')
          : (body?.message ?? t('webauthn_failed'));
      setWebauthnStatus('error');
      setWebauthnError(message);
    }
  }

  function onRecoveryDone() {
    setStep('done');
    if (isSetupTokenFlow) {
      window.location.assign(returnTo);
    } else {
      window.location.assign('/account/security');
    }
  }

  if (user?.localAuthManagedByIdp) {
    return null;
  }

  return (
    <AuthCard title={t('title')} subtitle={t('subtitle')}>
      {step !== 'recovery_codes' && step !== 'done' && !isSetupTokenFlow ? (
          <div role="tablist" aria-label={t('title')} {...stylex.props(tabStyles.bar)}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'totp'}
              data-testid="mfa-tab-totp"
              onClick={() => switchTab('totp')}
              {...stylex.props(tabStyles.tab, tab === 'totp' && tabStyles.tabActive)}
            >
              {t('tab_totp')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'webauthn'}
              data-testid="mfa-tab-webauthn"
              onClick={() => switchTab('webauthn')}
              {...stylex.props(tabStyles.tab, tab === 'webauthn' && tabStyles.tabActive)}
            >
              {t('tab_webauthn')}
            </button>
          </div>
        ) : null}

        {topError ? (
          <Alert type="error" role="alert">
            {topError}
          </Alert>
        ) : null}

        {step === 'recovery_codes' ? (
          <RecoveryCodesStep codes={recoveryCodes} onDone={onRecoveryDone} />
        ) : tab === 'totp' ? (
          alreadyEnrolled ? (
            <Alert type="info" role="status">
              {t('already_enrolled')}
            </Alert>
          ) : enroll.isPending || !enroll.data ? (
            <p>{t('totp_starting')}</p>
          ) : (
            <TotpStep
              secret={enroll.data.secret}
              provisioningUri={enroll.data.provisioning_uri}
              onSubmit={onTotpSubmit}
              error={totpInlineError}
              submitting={confirm.isPending}
            />
          )
      ) : (
        <WebAuthnStep
          onRegister={onWebAuthnRegister}
          status={webauthnStatus}
          error={webauthnError}
          supported={supportsWebAuthn}
        />
      )}
    </AuthCard>
  );
}

export const Route = createFileRoute('/mfa/setup')({
  validateSearch: searchSchema,
  component: MfaSetupPage,
});
